/**
 * Comparateur (SPEC.md §6).
 *
 * Il produit ce que l'expérience a le droit d'afficher : un **écart entre
 * prédiction et mesure, exprimé en multiples de l'incertitude déclarée**, et un
 * **compte d'hypothèses libres**. Jamais un verdict. Aucune fonction d'ici ne
 * renvoie « modèle correct » ou « modèle prouvé » — cette notion n'existe pas
 * dans le vocabulaire du module.
 *
 * `compare` est le **seul** chemin vers des résidus, et il commence par le
 * contrôle de comparabilité. Il n'y a donc pas de façon d'obtenir un écart sans
 * données d'observation externes : le garde-fou est dans le chemin, pas posé à
 * côté en espérant qu'on l'appelle.
 */

import { coreFr } from "../i18n/core-fr.js";
import { fr } from "../i18n/fr.js";
import { DATASET_LIMITS } from "../data/limits.js";
import { abs } from "./math.js";
import { checkComparability } from "./comparability.js";
import type { Dataset, Row } from "./dataset.js";
import { instantOf } from "./dataset.js";
import type { ModelDescriptor, Prediction, ShadowInput } from "./models.js";
import { countFreeHypotheses } from "./models.js";
import type { Dimension, Quantity } from "./units.js";
import { dimensionOf, dimensionless, isUnit, subtract, UnitError, valueIn } from "./units.js";

/** Colonnes dont l'expérience du gnomon a besoin pour prédire. */
const REQUIRED_INPUTS = ["utc", "latitude", "longitude", "gnomonHeight"] as const;

export type ShadowCandidateModel =
  | {
      readonly kind: "computed";
      readonly descriptor: ModelDescriptor;
      readonly predict: (input: ShadowInput) => Prediction;
    }
  | {
      /** Modèle annoncé et compté, mais volontairement non résolu. */
      readonly kind: "declared-only";
      readonly descriptor: ModelDescriptor;
    };

export interface ShadowComparisonRequest {
  readonly dataset: Dataset | undefined;
  readonly observable: string;
  readonly models: readonly ShadowCandidateModel[];
}

export type ObservationPrediction =
  | {
      readonly kind: "predicted";
      readonly value: Quantity<"length">;
      /** Prédiction moins mesure. Le signe est conservé : il porte du sens. */
      readonly difference: Quantity<"length">;
      readonly inSigma: Quantity<"dimensionless"> | undefined;
    }
  | { readonly kind: "absent"; readonly reason: string };

export interface ComparedObservation {
  readonly siteId: string;
  readonly instant: Quantity<"time">;
  readonly observed: Quantity<"length">;
  readonly uncertainty: Quantity<"length">;
  /** Une prédiction par identifiant de modèle calculé, sans couplage par index. */
  readonly predictions: ReadonlyMap<string, ObservationPrediction>;
}

export interface ShadowModelComparison {
  readonly model: ModelDescriptor;
  readonly calculation: ShadowCandidateModel["kind"];
  readonly freeHypotheses: Quantity<"dimensionless">;
  readonly predictedCount: Quantity<"dimensionless">;
  /** Absents si le modèle n'a produit aucune prédiction. */
  readonly meanAbsoluteSigma: Quantity<"dimensionless"> | undefined;
  readonly worstAbsoluteSigma: Quantity<"dimensionless"> | undefined;
}

export type ShadowComparison =
  | { readonly ok: false; readonly reasons: readonly string[] }
  | {
      readonly ok: true;
      readonly observationCount: Quantity<"dimensionless">;
      readonly observations: readonly ComparedObservation[];
      readonly models: readonly ShadowModelComparison[];
    };

interface Measurement {
  readonly siteId: string;
  readonly input: ShadowInput;
  readonly observed: Quantity<"length">;
  readonly uncertainty: Quantity<"length">;
}

function textOf(row: Row, column: string): string | undefined {
  const cell = row[column];
  return cell?.kind === "text" ? cell.value : undefined;
}

/**
 * Lit une grandeur **en vérifiant sa dimension**.
 *
 * Sans ce contrôle, un CSV déclarant `latitude[m]` passerait le chargeur — les
 * mètres sont une unité valide — et produirait un écart parfaitement calculé et
 * parfaitement absurde. L'unité obligatoire ne suffit pas ; c'est la dimension
 * attendue qui doit être exigée.
 */
function quantityOf<D extends Dimension>(
  row: Row,
  column: string,
  expected: D,
): Quantity<D> | undefined {
  const cell = row[column];
  if (cell?.kind !== "quantity") return undefined;
  if (!isUnit(cell.value.unit) || dimensionOf(cell.value.unit) !== expected) return undefined;
  return cell.value as Quantity<D>;
}

function wrongDimension(dataset: Dataset, column: string, expected: Dimension): boolean {
  const found = dataset.columns.find((c) => c.name === column);
  return found?.unit !== undefined && (!isUnit(found.unit) || dimensionOf(found.unit) !== expected);
}

/** Extrait les relevés exploitables ; signale les colonnes manquantes. */
function readMeasurements(
  dataset: Dataset,
  observable: string,
): { measurements: readonly Measurement[]; reasons: readonly string[] } {
  const reasons: string[] = [];

  for (const column of REQUIRED_INPUTS) {
    if (!dataset.columns.some((c) => c.name === column)) {
      reasons.push(
        coreFr.comparison.missingColumn(String(column)),
      );
    }
  }
  const EXPECTED: ReadonlyArray<readonly [string, Dimension]> = [
    ["latitude", "angle"],
    ["longitude", "angle"],
    ["gnomonHeight", "length"],
    [observable, "length"],
  ];
  for (const [column, dimension] of EXPECTED) {
    if (wrongDimension(dataset, column, dimension)) {
      reasons.push(
        coreFr.comparison.incompatibleColumn(String(column), String(dimension)),
      );
    }
  }

  if (reasons.length > 0) return { measurements: [], reasons };

  const measurements: Measurement[] = [];

  if (dataset.rows.length > DATASET_LIMITS.rows) return { measurements: [], reasons: [fr.datasetTooManyRows(DATASET_LIMITS.rows)] };
  for (const [index, row] of dataset.rows.entries()) {
    const instant = instantOf(row, "utc");
    if (row["utc"]?.kind === "text" && instant === undefined) {
      reasons.push(fr.comparisonInvalidRow(index + 1));
      continue;
    }
    const latitude = quantityOf(row, "latitude", "angle");
    const longitude = quantityOf(row, "longitude", "angle");
    const gnomonHeight = quantityOf(row, "gnomonHeight", "length");
    const cell = row[observable];

    if (
      instant === undefined ||
      latitude === undefined ||
      longitude === undefined ||
      gnomonHeight === undefined ||
      cell?.kind !== "quantity" ||
      cell.uncertainty === undefined
    ) {
      continue;
    }

    if (invalidMeasurement(latitude, longitude, gnomonHeight, cell.value, cell.uncertainty)) {
      reasons.push(fr.comparisonInvalidRow(index + 1));
      continue;
    }
    measurements.push({
      siteId: textOf(row, "siteId") ?? "",
      input: { instant, latitude, longitude, gnomonHeight },
      observed: cell.value as Quantity<"length">,
      uncertainty: cell.uncertainty as Quantity<"length">,
    });
  }

  return { measurements, reasons };
}

function invalidMeasurement(
  latitude: Quantity, longitude: Quantity, height: Quantity, observed: Quantity, uncertainty: Quantity,
): boolean {
  try {
    return abs(valueIn(latitude, "deg")) > 90 || abs(valueIn(longitude, "deg")) > 180 ||
      !(valueIn(height, "m") > 0) || valueIn(observed, "m") < 0 || !(valueIn(uncertainty, "m") > 0);
  } catch { return true; }
}

function predictionFor(
  candidate: Extract<ShadowCandidateModel, { kind: "computed" }>,
  measurement: Measurement,
): ObservationPrediction {
  const prediction = candidate.predict(measurement.input);
  if (prediction.kind !== "shadow") return { kind: "absent", reason: prediction.reason };

  const difference = subtract(prediction.length, measurement.observed);
  const sigma = valueIn(measurement.uncertainty, "m");
  return {
    kind: "predicted",
    value: prediction.length,
    difference,
    inSigma: sigma > 0 ? dimensionless(valueIn(difference, "m") / sigma) : undefined,
  };
}

export function compare(request: ShadowComparisonRequest): ShadowComparison {
  try { return compareMeasurements(request); }
  catch (error) {
    if (!(error instanceof UnitError)) throw error;
    return { ok: false, reasons: [coreFr.comparison.unrepresentableResult] };
  }
}

function compareMeasurements(request: ShadowComparisonRequest): ShadowComparison {
  const verdict = checkComparability({
    models: request.models.map((candidate) => candidate.descriptor.id),
    dataset: request.dataset,
    observable: request.observable,
  });

  if (!verdict.ok) return { ok: false, reasons: verdict.reasons };

  // `checkComparability` garantit le dataset ; la vérification reste pour que
  // le type le sache aussi.
  const { dataset } = request;
  if (dataset === undefined) {
    return { ok: false, reasons: [coreFr.comparison.missingDataset] };
  }

  const { measurements, reasons } = readMeasurements(dataset, request.observable);
  if (reasons.length > 0) return { ok: false, reasons };
  if (measurements.length === 0) {
    return {
      ok: false,
      reasons: [
        coreFr.comparison.noUsableMeasurements,
      ],
    };
  }

  const observationCount = dimensionless(measurements.length);
  const computed = request.models.filter(
    (candidate): candidate is Extract<ShadowCandidateModel, { kind: "computed" }> =>
      candidate.kind === "computed",
  );

  const observations = measurements.map((measurement): ComparedObservation => {
    const predictions = new Map<string, ObservationPrediction>();
    for (const candidate of computed) {
      predictions.set(candidate.descriptor.id, predictionFor(candidate, measurement));
    }
    return {
      siteId: measurement.siteId,
      instant: measurement.input.instant,
      observed: measurement.observed,
      uncertainty: measurement.uncertainty,
      predictions,
    };
  });

  const models = request.models.map((candidate): ShadowModelComparison => {
    const sigmas = observations
      .map((observation) => observation.predictions.get(candidate.descriptor.id))
      .filter(
        (prediction): prediction is Extract<ObservationPrediction, { kind: "predicted" }> =>
          prediction?.kind === "predicted",
      )
      .map((prediction) => prediction.inSigma)
      .filter((sigma): sigma is Quantity<"dimensionless"> => sigma !== undefined)
      .map((sigma) => abs(valueIn(sigma, "1")));

    const mean = sigmas.reduce((sum, value) => sum + value / sigmas.length, 0);
    const worst = sigmas.reduce((max, value) => (value > max ? value : max), 0);

    return {
      model: candidate.descriptor,
      calculation: candidate.kind,
      freeHypotheses: countFreeHypotheses(candidate.descriptor, observationCount),
      predictedCount: dimensionless(observations.filter((observation) =>
        observation.predictions.get(candidate.descriptor.id)?.kind === "predicted").length),
      meanAbsoluteSigma:
        sigmas.length > 0 ? dimensionless(mean) : undefined,
      worstAbsoluteSigma: sigmas.length > 0 ? dimensionless(worst) : undefined,
    };
  });

  return { ok: true, observationCount, observations, models };
}
